// "Edit name, note, end date" and "Change plan" (SPEC §7.3). Both go through the save flow with conflict and
// orphan confirmations.

import { changePlan, updateGroup } from "../../api/groups";
import type { Group } from "../../api/types";
import { t } from "../../i18n/i18n";
import { requireSchool } from "../../session";
import { createButton, createElement } from "../../ui/dom";
import { createInputField, createTextAreaField, type Field } from "../../ui/forms";
import { createSlotsEditor, type SlotsEditor } from "../../ui/slotsEditor";
import type { ReferenceData } from "../referenceData";
import { runGroupSave } from "./groupSaveFlow";
import { createRoomField, createTeacherField } from "./planFields";

/** The identity form; calls onSaved after a successful save. */
export function buildIdentityForm(group: Group, onSaved: () => void): HTMLElement {
  const form: HTMLDivElement = createElement("div", "card");
  const name: Field<HTMLInputElement> = createInputField(t("groups.name"), "text", group.name);
  const endDate: Field<HTMLInputElement> = createInputField(t("groups.endDate"), "date", group.endDate ?? "");
  const note: Field<HTMLTextAreaElement> = createTextAreaField(t("app.note"), group.note ?? "");
  const messages: HTMLDivElement = createElement("div");
  const save: HTMLButtonElement = createButton(t("app.save"), (): Promise<void> => runGroupSave(
    (confirm: boolean, confirmOrphans: boolean) => updateGroup(group.id, {
      name: name.input.value, note: note.input.value, endDate: endDate.input.value || null,
      version: group.version, confirm, confirmOrphans,
    }), messages, onSaved), "button-primary");
  const grid: HTMLDivElement = createElement("div", "form-grid");
  grid.append(name.wrapper, endDate.wrapper);
  form.append(createElement("h2", "", t("groups.editIdentity")), grid, note.wrapper, messages, save);
  return form;
}

/** The plan form, prefilled with the current period; "Apply from" defaults to today (SPEC §4.6). */
export function buildPlanForm(group: Group, reference: ReferenceData, onSaved: () => void): HTMLElement {
  const form: HTMLDivElement = createElement("div", "card");
  const applyFrom: Field<HTMLInputElement> = createInputField(t("groups.applyFrom"), "date", requireSchool().today);
  applyFrom.input.min = requireSchool().today; // Past lessons keep their plan (SPEC §4.6).
  const teacher: Field<HTMLSelectElement> = createTeacherField(reference, group.courseId,
    group.currentPeriod.teacherId);
  const room: Field<HTMLSelectElement> = createRoomField(reference, group.currentPeriod.classroomId);
  const slots: SlotsEditor = createSlotsEditor(group.currentPeriod.slots, requireSchool().defaultLessonCells);
  const messages: HTMLDivElement = createElement("div");
  const save: HTMLButtonElement = createButton(t("app.save"), (): Promise<void> => runGroupSave(
    (confirm: boolean, confirmOrphans: boolean) => changePlan(group.id, {
      applyFrom: applyFrom.input.value, teacherId: teacher.input.value, classroomId: room.input.value,
      slots: slots.getSlots(), version: group.version, confirm, confirmOrphans,
    }), messages, onSaved), "button-primary");
  const grid: HTMLDivElement = createElement("div", "form-grid");
  grid.append(applyFrom.wrapper, teacher.wrapper, room.wrapper);
  form.append(createElement("h2", "", t("groups.changePlan")), grid, slots.element, messages, save);
  return form;
}
